import { useCallback, useEffect, useRef, useState } from 'react'
import { isNil } from 'es-toolkit'
import { getHarvestLayerData, type HoverLayer } from 'api/field'
import { HoverDataClient } from 'common/harvest-hover/hover-data-client'
import type { HoverHit } from 'common/harvest-hover/protocol'

export type HoverDataStatus =
  'idle' | 'preparing' | 'downloading' | 'ready' | 'error'

/**
 * Loads one harvest layer into the hover worker and answers pointer queries
 * against it. The worker — and every byte of the layer — is released when the
 * harvest or layer changes or the caller unmounts.
 */
export default function useHarvestHoverData(
  harvestId: number | null | undefined,
  layer: HoverLayer | null,
) {
  const [status, setStatus] = useState<HoverDataStatus>('idle')
  // 0–1 while downloading, null when the size is unknown.
  const [progress, setProgress] = useState<number | null>(null)
  const [hit, setHit] = useState<HoverHit | null>(null)
  const clientRef = useRef<HoverDataClient | null>(null)

  useEffect(() => {
    setHit(null)
    setProgress(null)
    if (isNil(harvestId) || isNil(layer)) {
      setStatus('idle')
      return
    }

    let cancelled = false
    setStatus('preparing')

    getHarvestLayerData(harvestId, layer)
      .then(({ url, uncompressedSize }) => {
        if (cancelled) return
        const worker = new Worker(
          new URL(
            '../common/harvest-hover/harvest-hover.worker.ts',
            import.meta.url,
          ),
        )
        const client = new HoverDataClient(worker, {
          onProgress: (loadedBytes, totalBytes) =>
            setProgress(
              totalBytes ? Math.min(loadedBytes / totalBytes, 1) : null,
            ),
          onReady: () => setStatus('ready'),
          onError: () => setStatus('error'),
          onHit: setHit,
        })
        clientRef.current = client
        setStatus('downloading')
        client.load(url, uncompressedSize)
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
      clientRef.current?.terminate()
      clientRef.current = null
    }
  }, [harvestId, layer])

  const setPointer = useCallback((x: number, y: number, tolerance: number) => {
    clientRef.current?.setPointer(x, y, tolerance)
  }, [])

  const clearPointer = useCallback(() => {
    clientRef.current?.clearPointer()
  }, [])

  return { status, progress, hit, setPointer, clearPointer }
}
